import { FC, ReactNode, useState } from 'react';
import find from 'lodash/find';
import { Link } from 'react-router-dom';
import isNil from 'lodash/isNil';
import flatMap from 'lodash/flatMap';
import classNames from 'classnames';

import Utils from '../utils';
import { CategoryPageBucketEntry } from '../../../models/CategoryPage';
import { DropDownMenu } from '../common/DropDownMenu';
import { useTranslation } from 'react-i18next';
import { getGenreId, getGenreIdFromUrl, getSubGenreId } from '../common/translationMappers/genreIdMapper';

type LoanFormatType = {
    name: string;
    key: string;
    icon: string;
};

interface Props {
    path?: string;
    queryParamString?: string;
    navCategories?: [];
    breadcrumbs: {
        label: string;
        activeBucket?: string;
        key: string;
        buckets: readonly CategoryPageBucketEntry[];
    };
    loanFormat?: LoanFormatType;
    withLoanFormat?: any;
    subNavData?: any;
    fixedLabel?: string;
    customGenreBase?: string;
    customExtension?: string;
    floatingClass?: string;
}

const BreadcrumbNav: FC<Props> = ({
    path,
    queryParamString,
    navCategories,
    breadcrumbs,
    loanFormat,
    withLoanFormat,
    subNavData = {},
    fixedLabel,
    customGenreBase,
    customExtension,
    floatingClass = 'fl-left',
    ...props
}) => {
    function buildUrl(path) {
        return `${Utils.buildUrl(path)}${queryParamString || ''}`;
    }
    const { t } = useTranslation();
    const [expanded, setExpanded] = useState(false);

    function findAllBucket() {
        let bucket: {} | null = null;
        if (navCategories && navCategories.length > 0) {
            const category = find(navCategories, {
                name: breadcrumbs.label,
                loanFormat: loanFormat?.name
            });
            if (category && category.items && category.items.length > 0) {
                const item = category.items[category.items.length - 1];
                if (item) {
                    bucket = {
                        key: '',
                        value: item.name
                    };
                }
            }
        }
        return bucket;
    }

    if (isNil(breadcrumbs) || isNil(breadcrumbs.activeBucket)) {
        return <div />;
    }

    const rootPath = path || 'catalogue';
    const crumbs: ReactNode[] = [];
    const basePaths = [rootPath];

    if (withLoanFormat && loanFormat) {
        basePaths.push(loanFormat.key.toLowerCase());

        crumbs.push(
            <BreadcrumbPart
                {...props}
                label={
                    <>
                        <i className="bolinda-icons">{loanFormat.icon}</i>
                        <span>{loanFormat.name}</span>
                    </>
                }
                paths={basePaths.concat('featured')}
                key="featured"
            />
        );
    }

    const breadcrumbKey = breadcrumbs.key;
    const basePath = find(subNavData, { path: breadcrumbKey });
    if (basePath) {
        crumbs.push(
            <BreadcrumbPart {...props} label={basePath.label} paths={basePaths.concat(breadcrumbKey)} key="base" />
        );
    } else if (!isNil(fixedLabel)) {
        const path = customGenreBase ? customGenreBase : 'featured';

        const breadcrumbPath = !customExtension
            ? basePaths.concat([path])
            : basePaths.concat([path, encodeURIComponent(customExtension)]);

        crumbs.push(
            <BreadcrumbPart {...props} label={fixedLabel} paths={breadcrumbPath} query={queryParamString} key="base" />
        );
    } else {
        crumbs.push(
            <BreadcrumbPart
                {...props}
                label="Categories"
                paths={basePaths.concat('featured')}
                query={{ showCategories: true }}
                key="categories"
            />
        );
        crumbs.push(
            <BreadcrumbPart {...props} label={breadcrumbs.label} paths={basePaths.concat(breadcrumbKey)} key="base" />
        );
    }

    let buckets = breadcrumbs.buckets;
    const activeBucket = breadcrumbs.activeBucket;
    let lookupKey: string | null = null;

    const activeBucketParts = activeBucket?.split('->') ?? [];
    activeBucketParts.forEach((part, i) => {
        lookupKey = lookupKey ? `${lookupKey}->${part}` : part;

        // add an "all ..." option to buckets
        if (i === 0 && buckets && buckets[buckets.length - 1]?.key !== '') {
            const allBucket = findAllBucket();
            if (allBucket) {
                //buckets.push(allBucket);
            }
        }

        const activeBucket = find(buckets, { key: lookupKey });
        const genreId = getGenreId(activeBucket?.key);
        const subeGenreId = getSubGenreId(activeBucket?.key);
        if (activeBucket) {
            if (i < activeBucketParts.length - 1) {
                const baseUrl = customGenreBase ? customGenreBase : breadcrumbKey;
                const breadcrumbPath = customGenreBase
                    ? basePaths.concat([customGenreBase, encodeURIComponent(part)])
                    : buildUrl(basePaths.concat([baseUrl, encodeURIComponent(part)]));
                crumbs.push(
                    <BreadcrumbPart
                        {...props}
                        label={t(`filters:genre.${genreId}`, {
                            defaultValue: activeBucket.value
                        })}
                        key={i}
                        paths={breadcrumbPath}
                    />
                );
            } else {
                crumbs.push(
                    <li key={i}>
                        <DropDownMenu
                            expanded={expanded}
                            onToggleClick={setExpanded}
                            onClickOutside={setExpanded}
                            align="left"
                            label={t(`filters:genre.${subeGenreId ?? genreId}`, {
                                defaultValue: activeBucket.value || activeBucket.key
                            })}
                            showAsButton={false}
                            showActiveArrow={false}
                            iconSizeClass="md-upper"
                        >
                            {buckets.map((bucket, j) => {
                                const keys = bucket.key.split('->');
                                keys.forEach((k: string | number | boolean, i: string | number) => {
                                    keys[i] = encodeURIComponent(k);
                                });

                                const [parentGenre, subGenre] = keys;
                                const label = t(
                                    `filters:genre.${getGenreIdFromUrl(subGenre) ?? getGenreIdFromUrl(parentGenre)}`,
                                    {
                                        defaultValue: bucket.value || bucket.key
                                    }
                                );
                                const baseUrl = customGenreBase ? customGenreBase : breadcrumbKey;
                                const paths = [...basePaths, baseUrl, ...keys];

                                return (
                                    <li key={j}>
                                        <Link to={buildUrl(paths)} onClick={() => setExpanded(false)}>
                                            {label}
                                        </Link>
                                    </li>
                                );
                            })}
                        </DropDownMenu>
                    </li>
                );
            }
            buckets = activeBucket.buckets;
        }
    });

    return (
        <ul className={classNames('bc-nav', floatingClass)} data-displayname="BreadcrumbNav">
            {crumbs[0]}
            {flatMap(crumbs.slice(1), (crumb: string, index: number) => [
                <li key={`bc_item_${index}`}>
                    <i className="material-icons md-big">keyboard_arrow_right</i>
                </li>,
                crumb
            ])}
        </ul>
    );
};
export default BreadcrumbNav;

const BreadcrumbPart = props => {
    let label = props.label;
    if (props.paths) {
        const link = {
            pathname: Utils.buildUrl(props.paths),
            search: props.query
        };
        label = (
            <Link className={props.className} to={link}>
                {props.label}
            </Link>
        );
    }

    return <li>{label}</li>;
};
